import { parseAsString, useQueryStates } from 'nuqs';
import { useCallback, useMemo } from 'react';
import type { FilterField } from './filters/filterUtils';
import {
  buildFilterParsers,
  buildGraphQLFilter,
  clearPayload,
  hasActiveFilters,
  normalizeFilters,
} from './filters/filterUtils';

type QueryParamParser<T> = {
  parse: (value: string | null) => T;
  serialize: (value: T) => string;
  defaultValue?: T;
};

export interface ChannelFilters {
  search: string;
}

const fields = [
  { key: 'search', type: 'search', graphKey: 'search' },
] satisfies Array<FilterField<keyof ChannelFilters>>;

const filterParsers = buildFilterParsers<keyof ChannelFilters>(
  fields
) as { [K in keyof ChannelFilters]: QueryParamParser<ChannelFilters[K]>; };

const allParsers = {
  ...filterParsers,
  id: parseAsString.withDefault(''),
};

/**
 * Hook to manage channel filters via URL query params.
 * This is the single source of truth for channel filter state.
 * No prop drilling needed - use this hook anywhere you need channel filters.
 */
export function useChannelFilters() {
  const [state, setUrlState] = useQueryStates(allParsers);

  const normalizedFilters = useMemo(
    () =>
      normalizeFilters<ChannelFilters, keyof ChannelFilters>(state, fields),
    [state]
  );

  const setSearch = useCallback(
    (value: string) => {
      setUrlState({ search: value || null });
    },
    [setUrlState]
  );

  const setFilters = useCallback(
    (newFilters: Partial<ChannelFilters>) => {
      if (newFilters.search !== undefined) {
        setUrlState({ search: newFilters.search || null });
      }
    },
    [setUrlState]
  );

  const clearFilters = useCallback(() => {
    setUrlState(clearPayload(fields));
  }, [setUrlState]);

  const graphQLFilter = useMemo(
    () => buildGraphQLFilter(state, fields),
    [state]
  );

  const selectedId = state.id || null;

  const setSelectedId = useCallback(
    (id: string | null) => {
      setUrlState({ id: id || null });
    },
    [setUrlState]
  );

  const active = useMemo(() => hasActiveFilters(state, fields), [state]);

  return {
    filters: normalizedFilters,
    setSearch,
    setFilters,
    clearFilters,
    hasActiveFilters: active,
    graphQLFilter,
    selectedId,
    setSelectedId,
  };
}
